import { useEffect, useState } from "react";
import { createProjectService, updateProjectService } from "@/api/projectServicesMaintenance/api";
import { showSuccessToast } from "@/hooks/use-toast";
import { FormDialog } from "@/components/common/Dialog";
import { ProjectService } from "@/types/domain";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Input } from "@/components/common/Input";
import { ButtonVariant } from "@/components/shadcn/button";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";

export type CreateEditProjectServiceDialogProps = {
  services: ProjectService[];
  callBack: () => Promise<void>;
  serviceToEdit?: ProjectService;
  triggerVariant?: ButtonVariant;
  triggerText?: string;
  triggerIcon?: IconProp;
  triggerClassName?: string;
}

export const CreateEditProjectServiceDialog = ({ services, callBack, serviceToEdit, triggerVariant, triggerIcon, triggerText, triggerClassName }: CreateEditProjectServiceDialogProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [serviceName, setServiceName] = useState("");
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (dialogOpen) {
      setServiceName(serviceToEdit?.name ?? "");
      setNameError(null);
    }
  }, [dialogOpen, serviceToEdit]);

  const isDuplicateService = (services: ProjectService[], name: string) => {
    return services.some(
      s =>
        s.name.trim().toLowerCase() === name.trim().toLowerCase() &&
        (!serviceToEdit || s.id !== serviceToEdit.id)
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDuplicateService(services, serviceName)) {
      setNameError("A service with this name already exists.");
      return;
    }
    setLoading(true);
    try {
      if (serviceToEdit) {
        await updateProjectService({ ...serviceToEdit, name: serviceName });
        showSuccessToast("Service updated successfully.");
      } else {
        await createProjectService({ name: serviceName });
        showSuccessToast("Service created successfully.");
      }
      setDialogOpen(false);
      await callBack();
    } catch (err) {
      setNameError("Failed to save service.");
    } finally {
      setLoading(false);
    }
  };

  const handleServiceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setServiceName(e.target.value);
    setNameError(null);
  };

  return (
    <>
      <LoadingOverlay 
        isVisible={loading} 
        message={serviceToEdit ? "Updating service..." : "Creating service..."} 
      />
      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        triggerText={triggerText}
        triggerIcon={triggerIcon}
        title={serviceToEdit ? "Edit Project Service" : "Create Project Service"}
        onSubmit={handleSubmit}
        submitText={loading ? "Saving..." : "Save"}
        cancelText="Cancel"
        loading={loading}
        disableSubmit={!serviceName}
        triggerVariant={triggerVariant}
        triggerClassName={triggerClassName}
      >
        <div className="grid gap-4 py-4">
          <div className="grid gap-1">
            <Input
              id="service-name"
              value={serviceName}
              onChange={handleServiceNameChange}
              disabled={loading}
              required={true}
              autoFocus={true}
              inErrorState={!!nameError}
              maxLength={20}
            />
            {nameError && (
              <span id="service-name-error" className="text-red-500 text-sm">
                {nameError}
              </span>
            )}
          </div>
        </div>
      </FormDialog>
    </>
  );
}
